// CreatePromotionalActivity.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Col,
  Form,
  Row,
  Input,
  Label,
  Button,
  ModalBody,
  ModalHeader,
  Modal,
  Table,
} from "reactstrap";
import Multiselect from "multiselect-react-dropdown";
import swal from "sweetalert";
import { Copy, Edit, Trash2 } from "react-feather";

// ✅ UPDATED API PATHS (as requested)
import {
  _Delete,
  _Get,
  _PostSave,
  _Put,
  ProductListView,
  SavePromotionsActivity,
} from "../import/ApiEndPoint/ApiCalling";
import {
  ActivityList,
  Delete_Activity,
  SaveActivity,
  Update_Activity,
} from "../import/ApiEndPoint/Api";

const CreatePromotionalActivity = () => {
  const navigate = useNavigate();

  // ===== Data =====
  const [ActivityLists, setActivityList] = useState([]);
  const [ProductList, setProductList] = useState([]);
  const [formData, setFormData] = useState({}); // for Add/Edit Activity modal
  const [selectedActivity, setSelectedActivity] = useState(null);

  // ===== UI State =====
  const [modal, setModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // ===== Promotion Builder State =====
  const [DiscountType, setDiscountType] = useState(""); // "Percentage Wise" | "Amount Wise" | "Product Wise"
  const [Status, setStatus] = useState("Active");

  // Percentage wise
  const [TotalAmount, setTotalAmount] = useState("");
  const [Discountpercent, setDiscountpercentage] = useState("");

  // Amount wise
  const [FlatAmount, setFlatAmount] = useState("");

  // Product wise rows
  const [productRows, setProductRows] = useState([
    {
      productId: "",
      targetQty: "",
      freeProduct: "",
      freeProductQty: "",
      discountPercentage: "",
      discountAmount: "",
    },
  ]);

  // ===== Helpers =====
  const toggle = () => setModal((m) => !m);

  const filteredActivities = ActivityLists.filter((data) => {
    const s = searchTerm.toLowerCase();
    return (
      (data.ActivityName || "").toLowerCase().includes(s) ||
      (data.CustomerName || "").toLowerCase().includes(s) ||
      (data.ActivityType || "").toLowerCase().includes(s)
    );
  });

  const loadLists = async () => {
    const userdata = JSON.parse(localStorage.getItem("userData"));
    try {
      const actRes = await _Get(ActivityList, userdata?.database);
      setActivityList(actRes?.Activity || []);
    } catch (e) {
      setActivityList([]);
    }
    try {
      const prodRes = await ProductListView(userdata?._id, userdata?.database);
      setProductList(prodRes?.Product || []);
    } catch (e) {
      setProductList([]);
    }
  };

  useEffect(() => {
    loadLists();
  }, []);

  // ====== ACTIVITY: Create/Update ======
  const calculateDays = (startDate, endDate) => {
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffTime = Math.abs(end - start);
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    }
    return 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((s) => ({ ...s, [name]: value }));
  };

  const handleSubmitActivity = async (e) => {
    e.preventDefault();
    const NoOfDays = calculateDays(formData?.FromDate, formData?.ToDate);
    const userinfo = JSON.parse(localStorage.getItem("userData"));

    const Data = {
      ActivityName: formData?.ActivityName,
      ActivityType: formData?.ActivityType,
      FromDate: formData?.FromDate,
      ToDate: formData?.ToDate,
      NoOfDays,
      status: formData?.status,
      database: userinfo?.database,
    };

    try {
      if (formData?.editActivity) {
        await _Put(Update_Activity, formData?.id, Data);
        swal("Success", "Updated Successfully");
      } else {
        await _PostSave(SaveActivity, Data);
        swal("Success", "Created Successfully");
      }
      // Refresh and reset
      await loadLists();
      setFormData({
        ActivityName: "",
        ActivityType: "",
        FromDate: "",
        ToDate: "",
        status: "Active",
        id: null,
        editActivity: false,
      });
      setModal(false);
    } catch (err) {
      swal("Error", "Something went wrong");
    }
  };

  const HandleDeleteActivity = async (row) => {
    try {
      await _Delete(Delete_Activity, row?._id);
      setActivityList((list) => list.filter((x) => x?._id !== row?._id));
      if (selectedActivity?._id === row?._id) setSelectedActivity(null);
    } catch (err) {
      // ignore
    }
  };

  // ====== PROMOTION BUILDER ======
  const submitPromotion = async (e) => {
    e.preventDefault();
    const user = JSON.parse(localStorage.getItem("userData"));
    if (!selectedActivity?._id) {
      swal("Select an Activity first.");
      return;
    }

    try {
      let payload = {
        activityId: selectedActivity?._id,
        status: Status,
        database: user?.database,
        created_by: user?._id,
      };

      if (DiscountType === "Percentage Wise") {
        if (!TotalAmount || !Discountpercent) {
          swal("Enter total amount and % discount.");
          return;
        }
        payload.percentageWise = [
          {
            totalAmount: +TotalAmount,
            percentageDiscount: +Discountpercent,
            status: Status,
            database: user?.database,
          },
        ];
      } else if (DiscountType === "Amount Wise") {
        if (!TotalAmount || !FlatAmount) {
          swal("Enter total amount and flat discount.");
          return;
        }
        payload.amountWise = [
          {
            totalAmount: +TotalAmount,
            percentageAmount: +FlatAmount,
            status: Status,
            database: user?.database,
          },
        ];
      } else if (DiscountType === "Product Wise") {
        const normalized = productRows
          .filter((r) => r.productId && r.targetQty) // minimally require these
          .map((r) => ({
            productId: r.productId,
            targetQty: +r.targetQty || 0,
            discountAmount: r.discountAmount ? +r.discountAmount : undefined,
            discountPercentage: r.discountPercentage
              ? +r.discountPercentage
              : undefined,
            freeProductQty: r.freeProductQty ? +r.freeProductQty : undefined,
            freeProduct: r.freeProduct || undefined,
            database: user?.database,
          }));

        if (normalized.length === 0) {
          swal("Add at least one valid product row.");
          return;
        }
        // backend in your code consumed productWise as an array of items, not nested
        payload.productWise = normalized;
      } else {
        swal("Choose a Discount Type.");
        return;
      }

      await SavePromotionsActivity(payload);
      swal("Success", "Promotion Submitted Successfully");
      navigate(-1);
    } catch (err) {
      swal("Error", "Something went wrong");
    }
  };

  // ====== Product rows handlers ======
  const addRow = () =>
    setProductRows((rows) => [
      ...rows,
      {
        productId: "",
        targetQty: "",
        freeProduct: "",
        freeProductQty: "",
        discountPercentage: "",
        discountAmount: "",
      },
    ]);

  const removeRow = (idx) =>
    setProductRows((rows) => rows.filter((_, i) => i !== idx));

  const onRowChange = (idx, key, value) =>
    setProductRows((rows) =>
      rows.map((r, i) => (i === idx ? { ...r, [key]: value } : r))
    );

  const onSelectProduct = (selectedList, selectedItem, idx, field) => {
    onRowChange(idx, field, selectedItem?._id || "");
  };
  const onRemoveProduct = (_selectedList, _removedItem, idx, field) => {
    onRowChange(idx, field, "");
  };

  // ===== Render =====
  return (
    <div style={{ padding: 10 }}>
      {/* --- Inline minimal styles (no SCSS) --- */}
      <style>{`
        .page-title { font-weight: 600; font-size: 24px; margin: 10px 0; }
        .actions { display: flex; gap: 8px; justify-content: flex-end; }
        .soft-card { padding: 12px; border: 1px solid #e5e7eb; border-radius: 10px; margin: 10px; }
        .label-sm { font-size: 12px; font-weight: 600; margin-bottom: 6px; }
        .muted { color: #64748b; font-size: 12px; }
        .pill { border:1px solid #e5e7eb; padding: 6px 10px; border-radius: 999px; display:inline-flex; gap:8px; align-items:center; background:#f8fafc }
        .section-title { font-weight: 600; font-size: 16px; margin: 8px 0; }
        .divider { height:1px; background:#e5e7eb; margin:10px 0 }
        .clickable-row:hover { background:#f8fafc; cursor:pointer; }
        .w-140 { width: 140px; }
        .w-160 { width: 160px; }
        .w-200 { width: 200px; }
        .nowrap { white-space: nowrap; }
      `}</style>

      <Card className="soft-card">
        {/* Header */}
        <Row className="align-items-center">
          <Col xs="12" lg="8">
            <div className="page-title">Create Promotional Activity</div>
          </Col>
          <Col xs="12" lg="4">
            <div className="actions">
              <Button
                color="info"
                onClick={() => {
                  setFormData({
                    ActivityName: "",
                    FromDate: "",
                    ToDate: "",
                    ActivityType: "",
                    status: "Active",
                    editActivity: false,
                    id: null,
                  });
                  toggle();
                }}
              >
                + Activity
              </Button>
              <Button color="danger" onClick={() => navigate(-1)}>
                Back
              </Button>
            </div>
          </Col>
        </Row>

        {/* Search + Selected Activity Summary */}
        <Row className="mt-2 align-items-start">
          <Col lg="3" md="4" sm="12">
            <Label className="label-sm">Search Activities</Label>
            <Input
              type="text"
              placeholder="Search by Activity / Customer / Type"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Col>
          <Col lg="9" md="8" sm="12" className="mt-2 mt-md-0">
            {selectedActivity ? (
              <div className="pill">
                <span className="nowrap">
                  <b>Promo-Code:</b> {selectedActivity?.Code}
                </span>
                <span
                  title="Copy"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    navigator.clipboard.writeText(selectedActivity?.Code || "");
                    swal("Copied!");
                  }}
                >
                  <Copy size={16} />
                </span>
                <span className="nowrap">
                  <b>Name:</b> {selectedActivity?.ActivityName}
                </span>
                <span className="nowrap">
                  <b>From:</b>{" "}
                  {String(selectedActivity?.FromDate || "").split("T")[0]}
                </span>
                <span className="nowrap">
                  <b>To:</b>{" "}
                  {String(selectedActivity?.ToDate || "").split("T")[0]}
                </span>
                <span className="nowrap">
                  <b>Status:</b> {selectedActivity?.status}
                </span>
              </div>
            ) : (
              <div className="muted">
                Select an activity to build promotions.
              </div>
            )}
          </Col>
        </Row>

        <div className="divider" />

        {/* Activities Table */}
        <div className="section-title">Activity List</div>
        <div className="table-responsive">
          <Table bordered hover responsive className="text-center">
            <thead style={{ background: "#f1f5f9" }}>
              <tr>
                <th>#</th>
                <th>Activity Name</th>
                <th>Code</th>
                <th>Type</th>
                <th>Start</th>
                <th>End</th>
                <th>Status</th>
                <th className="nowrap">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredActivities?.map((ele, i) => (
                <tr
                  key={ele?._id || i}
                  className="clickable-row"
                  onClick={() => setSelectedActivity(ele)}
                >
                  <th scope="row">{i + 1}</th>
                  <td>{ele?.ActivityName}</td>
                  <td>{ele?.Code}</td>
                  <td>{ele?.ActivityType}</td>
                  <td>{String(ele?.FromDate || "").split("T")[0]}</td>
                  <td>{String(ele?.ToDate || "").split("T")[0]}</td>
                  <td>{ele?.status}</td>
                  <td
                    style={{ cursor: "pointer" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span title="Edit">
                      <Edit
                        onClick={() => {
                          setFormData({
                            ActivityName: ele?.ActivityName,
                            FromDate: String(ele?.FromDate || "").split("T")[0],
                            ToDate: String(ele?.ToDate || "").split("T")[0],
                            ActivityType: ele?.ActivityType,
                            status: ele?.status || "Active",
                            editActivity: true,
                            id: ele?._id,
                          });
                          toggle();
                        }}
                        color="green"
                        size={18}
                      />
                    </span>{" "}
                    <span title="Delete">
                      <Trash2
                        onClick={() => HandleDeleteActivity(ele)}
                        color="red"
                        size={18}
                      />
                    </span>
                  </td>
                </tr>
              ))}
              {filteredActivities?.length === 0 && (
                <tr>
                  <td colSpan={8} className="muted">
                    No activities found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        <div className="divider" />

        {/* Promotion Builder */}
        <div className="section-title">Promotion Builder</div>
        <Form onSubmit={submitPromotion}>
          <Row className="align-items-end">
            <Col lg="3" md="4" sm="12" className="mb-2">
              <Label className="label-sm">Discount Type</Label>
              <Input
                type="select"
                value={DiscountType}
                onChange={(e) => setDiscountType(e.target.value)}
              >
                <option value="">-- Select --</option>
                <option value="Percentage Wise">Percentage Wise</option>
                <option value="Amount Wise">Amount Wise</option>
                <option value="Product Wise">Product Wise</option>
              </Input>
            </Col>

            <Col lg="3" md="4" sm="12" className="mb-2">
              <Label className="label-sm">Status</Label>
              <div>
                <Label className="me-2">
                  <input
                    type="radio"
                    name="promo_status"
                    value="Active"
                    checked={Status === "Active"}
                    onChange={() => setStatus("Active")}
                    style={{ marginRight: 6 }}
                  />
                  Active
                </Label>
                <Label className="me-2">
                  <input
                    type="radio"
                    name="promo_status"
                    value="Deactive"
                    checked={Status === "Deactive"}
                    onChange={() => setStatus("Deactive")}
                    style={{ marginRight: 6, marginLeft: 12 }}
                  />
                  Deactive
                </Label>
              </div>
            </Col>
          </Row>

          {/* Percentage Wise */}
          {DiscountType === "Percentage Wise" && (
            <Row className="mt-2">
              <Col lg="3" md="4" sm="6" className="mb-2">
                <Label className="label-sm">Total Amount</Label>
                <Input
                  type="number"
                  min="0"
                  value={TotalAmount}
                  onChange={(e) => setTotalAmount(e.target.value)}
                  placeholder="e.g. 10000"
                />
              </Col>
              <Col lg="3" md="4" sm="6" className="mb-2">
                <Label className="label-sm">Discount %</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={Discountpercent}
                  onChange={(e) => setDiscountpercentage(e.target.value)}
                  placeholder="e.g. 5"
                />
              </Col>
            </Row>
          )}

          {/* Amount Wise */}
          {DiscountType === "Amount Wise" && (
            <Row className="mt-2">
              <Col lg="3" md="4" sm="6" className="mb-2">
                <Label className="label-sm">Total Amount</Label>
                <Input
                  type="number"
                  min="0"
                  value={TotalAmount}
                  onChange={(e) => setTotalAmount(e.target.value)}
                  placeholder="e.g. 10000"
                />
              </Col>
              <Col lg="3" md="4" sm="6" className="mb-2">
                <Label className="label-sm">Flat Discount (₹)</Label>
                <Input
                  type="number"
                  min="0"
                  value={FlatAmount}
                  onChange={(e) => setFlatAmount(e.target.value)}
                  placeholder="e.g. 500"
                />
              </Col>
            </Row>
          )}

          {/* Product Wise */}
          {DiscountType === "Product Wise" && (
            <>
              <div className="mt-2" />
              <Table bordered responsive>
                <thead style={{ background: "#f8fafc" }}>
                  <tr>
                    <th style={{ minWidth: 260 }}>Product</th>
                    <th style={{ minWidth: 120 }}>Target Qty</th>
                    <th style={{ minWidth: 260 }}>Free Product (optional)</th>
                    <th style={{ minWidth: 120 }}>Free Qty</th>
                    <th style={{ minWidth: 150 }}>Discount %</th>
                    <th style={{ minWidth: 150 }}>Discount ₹</th>
                    <th style={{ minWidth: 80 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {productRows.map((row, idx) => (
                    <tr key={idx}>
                      <td>
                        <Multiselect
                          selectionLimit={1}
                          displayValue="Product_Title" // ⬅️ change if your key differs
                          isObject
                          options={ProductList}
                          onSelect={(sl, si) =>
                            onSelectProduct(sl, si, idx, "productId")
                          }
                          onRemove={(sl, si) =>
                            onRemoveProduct(sl, si, idx, "productId")
                          }
                          placeholder="Select product"
                        />
                      </td>
                      <td>
                        <Input
                          type="number"
                          min="0"
                          value={row.targetQty}
                          onChange={(e) =>
                            onRowChange(idx, "targetQty", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <Multiselect
                          selectionLimit={1}
                          displayValue="Product_Title" // ⬅️ change if your key differs
                          isObject
                          options={ProductList}
                          onSelect={(sl, si) =>
                            onSelectProduct(sl, si, idx, "freeProduct")
                          }
                          onRemove={(sl, si) =>
                            onRemoveProduct(sl, si, idx, "freeProduct")
                          }
                          placeholder="(Optional) Select free product"
                        />
                      </td>
                      <td>
                        <Input
                          type="number"
                          min="0"
                          value={row.freeProductQty}
                          onChange={(e) =>
                            onRowChange(idx, "freeProductQty", e.target.value)
                          }
                          placeholder="e.g. 1"
                        />
                      </td>
                      <td>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={row.discountPercentage}
                          onChange={(e) =>
                            onRowChange(
                              idx,
                              "discountPercentage",
                              e.target.value
                            )
                          }
                          placeholder="e.g. 5"
                        />
                      </td>
                      <td>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={row.discountAmount}
                          onChange={(e) =>
                            onRowChange(idx, "discountAmount", e.target.value)
                          }
                          placeholder="e.g. 25"
                        />
                      </td>
                      <td className="text-center">
                        {idx > 0 ? (
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => removeRow(idx)}
                          >
                            Remove
                          </Button>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <div className="d-flex justify-content-end">
                <Button color="primary" size="sm" onClick={addRow}>
                  + Add Row
                </Button>
              </div>
            </>
          )}

          <div className="d-flex justify-content-center mt-3">
            <Button color="primary" type="submit">
              Submit Promotion
            </Button>
          </div>
        </Form>
      </Card>

      {/* ========== Add/Edit Activity Modal ========== */}
      <Modal size="lg" isOpen={modal} toggle={toggle}>
        <ModalHeader toggle={toggle}>
          {formData?.editActivity ? "Edit Activity" : "Add Activity"}
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={handleSubmitActivity}>
            <Row>
              <Col lg="6" md="6" sm="12" className="mb-2">
                <Label className="label-sm">Activity Name</Label>
                <Input
                  required
                  type="text"
                  name="ActivityName"
                  placeholder="Enter Activity Name"
                  onChange={handleInputChange}
                  value={formData?.ActivityName || ""}
                />
              </Col>

              <Col lg="6" md="6" sm="12" className="mb-2">
                <Label className="label-sm">Start Date</Label>
                <Input
                  required
                  type="date"
                  name="FromDate"
                  onChange={handleInputChange}
                  value={formData?.FromDate || ""}
                />
              </Col>

              <Col lg="6" md="6" sm="12" className="mb-2">
                <Label className="label-sm">Activity Type</Label>
                <Input
                  type="select"
                  name="ActivityType"
                  value={formData?.ActivityType || ""}
                  onChange={handleInputChange}
                >
                  <option value="">Select Type</option>
                  <option value="Monthly">Monthly</option>
                  <option value="Quarterly">Quarterly</option>
                  <option value="HalfYearly">HalfYearly</option>
                  <option value="Yearly">Yearly</option>
                </Input>
              </Col>

              <Col lg="6" md="6" sm="12" className="mb-2">
                <Label className="label-sm">End Date</Label>
                <Input
                  required
                  type="date"
                  name="ToDate"
                  onChange={handleInputChange}
                  value={formData?.ToDate || ""}
                />
              </Col>

              <Col lg="6" md="6" sm="12" className="mb-2">
                <Label className="label-sm">Status</Label>
                <div>
                  <Label className="me-2">
                    <input
                      type="radio"
                      name="status"
                      value="Active"
                      checked={(formData?.status || "Active") === "Active"}
                      onChange={handleInputChange}
                      style={{ marginRight: 6 }}
                    />
                    Active
                  </Label>
                  <Label>
                    <input
                      type="radio"
                      name="status"
                      value="Deactive"
                      checked={formData?.status === "Deactive"}
                      onChange={handleInputChange}
                      style={{ marginRight: 6, marginLeft: 12 }}
                    />
                    Deactive
                  </Label>
                </div>
              </Col>
            </Row>

            <div className="d-flex justify-content-center">
              <Button color="primary" type="submit" className="mt-2">
                Submit
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default CreatePromotionalActivity;
